import React from 'react'
import { useUcs } from './contexts/UserCompanySettingsContext'
import checkFlag from './featureFlags'
import Tooltip from '@mui/material/Tooltip'
import { Typography } from '@mui/material'

/**
 * Conditionally renders children based on user permissions or roles.
 *
 * @param {string} requiredLimit - Permission limit to check (if empty, skipped)
 * @param {string} requiredRole - Role to verify (if empty, skipped)
 * @param {boolean} condition -
 * @param {React.ReactNode} readOnly - Content shown when permission check fails
 * @param {React.ReactNode} children - Content shown when permission check passes
 * @returns {React.ReactNode} Appropriate content based on permission status
 *
 * @example
 * // Disable a button if user is not an admin
 * <PermissionGate
 *   requiredRole='admin'
 *   readOnly={<button disabled={true}>Press Me</button>}
 * >
 *   <button>Press Me</button>
 * </PermissionGate>
 */
const PermissionGate = ({
  requiredLimit = '',
  requiredRole = '',
  condition = null,
  readOnly = <></>,
  toolTipText,
  toolTipArrow = true,
  toolTipPlacement = 'bottom',
  spanSettings = {},
  children,
}) => {
  const { isRole, checkLimit, loaded, error } = useUcs()
  const printErr = false;

  if (!loaded) {
    return readOnly
  }

  // Handle errors
  if (error.length > 0) {
    if (printErr) console.error('Permission check error:', error)
    return readOnly
  }

  let hasRequiredPermissions = false

  if (requiredLimit) {
    // confirm user has limit != 0
    hasRequiredPermissions = checkLimit(requiredLimit)
  }

  if (requiredRole) {
    // confirm user has required role
    hasRequiredPermissions = isRole(requiredRole)
  }

  if (condition !== null) {
    hasRequiredPermissions = condition
  }

  // temp flag control for dbEditor
  if (requiredRole === 'dashboardEditor' && !checkFlag('dashboardEditor')) {
    hasRequiredPermissions = true
  }

  // Render children or fallback based on permission check
  return hasRequiredPermissions ? (
    children
  ) : (
    <Tooltip title={<Typography>{toolTipText}</Typography>} arrow={toolTipArrow} placement={toolTipPlacement}>
      <span {...spanSettings}>{readOnly}</span>
    </Tooltip>
  )
}

export default PermissionGate
